import { Tooltip } from 'antd';
import { IoMdSearch } from 'react-icons/io';
import {
  SUMMARY_ENTITY_ALL,
  SUMMARY_ENTITY_LABELS,
  SUMMARY_ENTITY_SEED,
  SUMMARY_METRIC_COST_PER_HA,
  SUMMARY_METRIC_QUANTITY,
  SUMMARY_METRIC_TOTAL_COST,
  SUMMARY_TEMPORAL_MONTHLY,
} from 'constants/uiConstants';
import React from 'react';
import type {
  SummaryDataMetric,
  SummaryEntityCategory,
  SummaryTemporalLayout,
} from '../../../../redux/slices/InsightsSlice';
import { resolveTotalColumnLabel, scaleForDisplay } from './insightUtils';

const MONTH_LABELS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;


export interface SummaryGridRow {
  label: string;
  /** Crop name — only set in Seed mode. */
  group?: string;
  values: number[];
  /** Number of steps merged into this row. */
  mergeCount?: number;
  /** Unit the row's values are expressed in — Quantity metric only. */
  unit?: string;
}

interface SummaryGridProps {
  entityCategory: SummaryEntityCategory;
  dataMetric: SummaryDataMetric;
  temporalLayout: SummaryTemporalLayout;
  rows: SummaryGridRow[];
  /** Metric label (e.g. "Quantity", "Cost ($)") — used to make the Total column header specific rather than just "Total". */
  metricLabel: string;
  /**
   * Paired sub-column labels (e.g. Plan, Actual). With two or more, every
   * data column splits into one sub-column per side, and each row's
   * `values` interleave the sides column by column
   * ([Total·Plan, Total·Actual, No date·Plan, …]). Omitted = one column each.
   */
  sideLabels?: string[];
}

const formatValue = (value: number, isCost: boolean): string => {
  const abs = Math.abs(value);
  const formatted = new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(abs);
  if (isCost) {
    return `${value < 0 ? '-' : ''}$${formatted}`;
  }
  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

/** Total + (optionally) Unit + remaining data column headers, shared between the empty and populated states. */
const DataHeaderCells: React.FC<{
  dataColumns: string[];
  showUnit: boolean;
  totalColumnLabel: string;
  /** Sub-columns per data column — > 1 spans each header over its paired sides. */
  sideCount: number;
}> = ({ dataColumns, showUnit, totalColumnLabel, sideCount }) => (
  <>
    <th
      colSpan={sideCount > 1 ? sideCount : undefined}
      className="w-24 px-2 pb-2 text-right text-sm font-bold text-charcoal"
    >
      {totalColumnLabel}
    </th>
    {showUnit && (
      <th
        rowSpan={sideCount > 1 ? 2 : undefined}
        className="w-16 px-2 pb-2 text-center text-sm font-bold text-charcoal"
      >
        Unit
      </th>
    )}
    {dataColumns.slice(1).map((col) => (
      <th
        key={col}
        colSpan={sideCount > 1 ? sideCount : undefined}
        className="w-24 px-2 pb-2 text-right text-sm font-bold text-charcoal"
      >
        {col}
      </th>
    ))}
  </>
);

/** Second header row for paired columns — one Plan / Actual label per data column. */
const SideHeaderRow: React.FC<{
  dataColumns: string[];
  sideLabels: string[];
}> = ({ dataColumns, sideLabels }) => (
  <tr>
    {dataColumns.flatMap((col) =>
      sideLabels.map((side) => (
        <th
          key={`${col}-${side}`}
          className="w-24 px-2 pb-2 text-right text-xs font-semibold text-gray-dark"
        >
          {side}
        </th>
      ))
    )}
  </tr>
);

const MergeIcon: React.FC<{ count: number }> = ({ count }) => (
  <Tooltip
    title={
      <>
        <div>Matching entries are summed:</div>
        <div>Combined from {count} similar items.</div>
      </>
    }
  >
    <span className="ml-1 inline-flex cursor-help align-middle">
      <IoMdSearch
        className="text-gray-muted transition-transform hover:scale-150"
        size={12}
      />
    </span>
  </Tooltip>
);

/* Only the first entity column is sticky; Seed column scrolls
   naturally since its left offset depends on dynamic COL0 width. */
const STICKY_COL0 = 'sticky left-0 z-10 bg-white group-hover:bg-rowHover';
/* Body-row variant — the sticky cell must match its row's own alternating
   stripe (not always bg-white) or it visually breaks the striping pattern
   every other row once content scrolls behind it. */
const stickyCol0BodyClass = (rowBg: string): string =>
  `sticky left-0 z-10 ${rowBg} group-hover:bg-rowHover`;

const SummaryGrid: React.FC<SummaryGridProps> = ({
  entityCategory,
  dataMetric,
  temporalLayout,
  rows,
  metricLabel,
  sideLabels,
}) => {
  const sideCount = Math.max(sideLabels?.length ?? 1, 1);
  const isPaired = sideCount > 1;
  const headerRowSpan = isPaired ? 2 : undefined;
  const isMonthly = temporalLayout === SUMMARY_TEMPORAL_MONTHLY;
  const isCost = dataMetric === SUMMARY_METRIC_TOTAL_COST;
  // $/ha is currency-formatted like Total Cost, but never triggers the ALL
  // mode Income/Cost dual-row rendering (that's isCost/isAllCost's job).
  const isCurrency = isCost || dataMetric === SUMMARY_METRIC_COST_PER_HA;
  const isQuantity = dataMetric === SUMMARY_METRIC_QUANTITY;
  const isAll = entityCategory === SUMMARY_ENTITY_ALL;
  const isAllCost = isAll && isCost;
  // $/ha in All mode is also a blended income-minus-cost figure (see
  // computeSummary's netPerHa), so its footer reads "Net" too, even
  // though — unlike Total Cost — it renders as a single hidden-body row
  // rather than the Income/Cost dual-row layout.
  const isAllNet = isAllCost || (isAll && dataMetric === SUMMARY_METRIC_COST_PER_HA);
  // $/ha values per product are not additive — summing them produces a number
  // with no financial meaning. Only the All-entity $/ha is exempted because
  // computeSummary returns a single pre-computed net $/ha row for that case.
  const isRatePerHa = dataMetric === SUMMARY_METRIC_COST_PER_HA && !isAll;
  const hideBody = isAll && !isCost;
  const hasGroup = entityCategory === SUMMARY_ENTITY_SEED;
  const entityLabel = hasGroup
    ? 'Crop'
    : (SUMMARY_ENTITY_LABELS[entityCategory] ?? 'Item');

  // Quantity rows can each carry their own unit (derived from the product's
  // own recorded unit) — summing across rows with different units in the
  // footer isn't meaningful, so the total is only shown when they agree.
  const quantityUnits = new Set(
    rows.map((row) => row.unit).filter((unit): unit is string => !!unit)
  );
  const uniformQuantityUnit =
    isQuantity && quantityUnits.size === 1
      ? Array.from(quantityUnits)[0]
      : null;
  const quantityTotalAmbiguous = isQuantity && quantityUnits.size > 1;

  const dataColumns: string[] = isMonthly
    ? ['Total', 'No date', ...MONTH_LABELS]
    : ['Total'];

  const totalColumnLabel = resolveTotalColumnLabel(dataMetric, metricLabel);

  // In Seed mode the Total column shifts one position right
  // Data columns are no longer sticky (entity column width is dynamic)

  /* ── Empty state ──────────────────────────────────────────────── */
  if (rows.length === 0) {
    return (
      <div className="summary-grid-scroll flex w-0 min-w-full flex-1 flex-col overflow-x-auto">
        <table className="w-max border-collapse text-base">
          <thead>
            <tr>
              <th
                rowSpan={headerRowSpan}
                className={`max-w-[12rem] pb-2 pl-2 pr-6 text-left text-sm font-bold text-charcoal ${isMonthly ? STICKY_COL0 : ''}`}
              >
                {entityLabel}
              </th>
              {hasGroup && (
                <th
                  rowSpan={headerRowSpan}
                  className={`max-w-[12rem] pb-2 pr-6 text-left text-sm font-bold text-charcoal`}
                >
                  Variety
                </th>
              )}
              <DataHeaderCells
                dataColumns={dataColumns}
                showUnit={isQuantity}
                totalColumnLabel={totalColumnLabel}
                sideCount={sideCount}
              />
            </tr>
            {isPaired && (
              <SideHeaderRow
                dataColumns={dataColumns}
                sideLabels={sideLabels!}
              />
            )}
          </thead>
        </table>
        <div className="flex flex-1 items-center justify-center">
          <span className="text-base text-gray-muted">No summary data</span>
        </div>
      </div>
    );
  }

  /* ── Column totals ────────────────────────────────────────────── */
  const columnCount = dataColumns.length * sideCount;
  // Value indices before the Unit column (the Total column's sides) and after it.
  const leadIndices = Array.from({ length: sideCount }, (_, ci) => ci);
  const restIndices = Array.from(
    { length: columnCount - sideCount },
    (_, i) => i + sideCount
  );
  const totals = isAllCost
    ? Array.from({ length: columnCount }, (_, ci) => {
        const revenue = rows[0]?.values[ci] ?? 0;
        const cost = rows[1]?.values[ci] ?? 0;
        return revenue - cost;
      })
    : Array.from({ length: columnCount }, (_, ci) =>
        rows.reduce((sum, row) => sum + (row.values[ci] ?? 0), 0)
      );

  // The footer's own mass→tonnes decision, independent of any individual row's.
  const footerScale = scaleForDisplay(
    Math.max(...leadIndices.map((ci) => totals[ci] ?? 0)),
    uniformQuantityUnit ?? undefined
  );
  const footerDivisor = footerScale.unit === 't' ? 1000 : 1;

  /* ── Populated grid ───────────────────────────────────────────── */
  return (
    <div className="summary-grid-scroll w-0 min-w-full overflow-x-auto">
      <table className="w-max border-collapse text-base">
        <thead>
          <tr>
            <th
              rowSpan={headerRowSpan}
              className={`max-w-[12rem] pb-2 pl-2 pr-6 text-left text-sm font-bold text-charcoal ${isMonthly ? STICKY_COL0 : ''}`}
            >
              {entityLabel}
            </th>
            {hasGroup && (
              <th
                rowSpan={headerRowSpan}
                className={`max-w-[12rem] pb-2 pr-6 text-left text-sm font-bold text-charcoal`}
              >
                Variety
              </th>
            )}
            <DataHeaderCells
              dataColumns={dataColumns}
              showUnit={isQuantity}
              totalColumnLabel={totalColumnLabel}
              sideCount={sideCount}
            />
          </tr>
          {isPaired && (
            <SideHeaderRow
              dataColumns={dataColumns}
              sideLabels={sideLabels!}
            />
          )}
        </thead>
        {!hideBody && (
        <tbody>
          {rows.map((row, idx) => {
            // Mass rows read better as tonnes once the row's own total
            // crosses the threshold; liquid never auto-scales. Total and
            // monthly cells share one decision (based on the Total column)
            // so the unit stays consistent across a row.
            const rowScale = scaleForDisplay(
              Math.max(...leadIndices.map((ci) => row.values[ci] ?? 0)),
              row.unit
            );
            const rowDivisor = rowScale.unit === 't' ? 1000 : 1;
            const rowBg = idx % 2 === 0 ? 'bg-white' : 'bg-gray-50';
            return (
            <tr
              key={hasGroup ? `${row.group}-${row.label}` : row.label}
              className={`group hover:bg-rowHover ${rowBg}`}
            >
              <td
                className={`max-w-[12rem] py-1.5 pl-2 pr-6 text-base text-charcoal ${isMonthly ? stickyCol0BodyClass(rowBg) : ''}`}
              >
                {hasGroup ? row.group : row.label}
                {(row.mergeCount ?? 0) > 1 && (
                  <MergeIcon count={row.mergeCount!} />
                )}
              </td>
              {hasGroup && (
                <td
                  className={`max-w-[12rem] py-1.5 pr-6 text-base text-charcoal`}
                >
                  {row.label}
                </td>
              )}
              {leadIndices.map((ci) => (
                <td
                  key={ci}
                  className="w-24 px-2 py-1.5 text-right tabular-nums text-charcoal"
                >
                  {formatValue(
                    (isAllCost && row.label === 'Cost'
                      ? -(row.values[ci] ?? 0)
                      : (row.values[ci] ?? 0)) / rowDivisor,
                    isCurrency
                  )}
                </td>
              ))}
              {isQuantity && (
                <td className="w-16 px-2 py-1.5 text-center text-gray-muted">
                  {rowScale.unit ?? '—'}
                </td>
              )}
              {restIndices.map((ci) => {
                const rawValue = row.values[ci] ?? 0;
                const displayValue =
                  (isAllCost && row.label === 'Cost' ? -rawValue : rawValue) /
                  rowDivisor;
                return (
                  <td
                    key={ci}
                    className={`w-24 px-2 py-1.5 text-right tabular-nums text-charcoal`}
                  >
                    {formatValue(displayValue, isCurrency)}
                  </td>
                );
              })}
            </tr>
            );
          })}
        </tbody>
        )}
        <tfoot>
          <tr>
            <td
              className={`max-w-[12rem] py-1.5 pl-2 pr-6 text-right text-base font-bold text-charcoal ${isMonthly ? STICKY_COL0 : ''}`}
            >
              {hasGroup ? '' : isAllNet ? 'Net:' : 'Total:'}
            </td>
            {hasGroup && (
              <td
                className={`max-w-[12rem] py-1.5 pr-6 text-right text-base font-bold text-charcoal`}
              >
                Total:
              </td>
            )}
            {leadIndices.map((ci) => (
              <td
                key={ci}
                className={`w-24 px-2 py-1.5 text-right font-bold tabular-nums ${totals[ci] < 0 ? 'text-danger' : 'text-charcoal'}`}
              >
                {quantityTotalAmbiguous || isRatePerHa ? (
                  <Tooltip
                    title={
                      quantityTotalAmbiguous
                        ? 'Rows use different units — no single total applies'
                        : 'Per-hectare rates are not additive — no single total applies'
                    }
                  >
                    <span className="cursor-help font-normal text-gray-muted">
                      —
                    </span>
                  </Tooltip>
                ) : (
                  formatValue(
                    totals[ci] / footerDivisor,
                    isCurrency
                  )
                )}
              </td>
            ))}
            {isQuantity && (
              <td className="w-16 px-2 py-1.5 text-center font-bold text-gray-muted">
                {quantityTotalAmbiguous ? (
                  <Tooltip title="Rows use different units — no single total applies">
                    <span className="cursor-help font-normal">—</span>
                  </Tooltip>
                ) : (
                  (footerScale.unit ?? '—')
                )}
              </td>
            )}
            {restIndices.map((ci) => {
              const total = totals[ci];
              return (
                <td
                  key={ci}
                  className={`w-24 px-2 py-1.5 text-right font-bold tabular-nums ${total < 0 ? 'text-danger' : 'text-charcoal'}`}
                >
                  {quantityTotalAmbiguous || isRatePerHa ? (
                    <Tooltip
                      title={
                        quantityTotalAmbiguous
                          ? 'Rows use different units — no single total applies'
                          : 'Per-hectare rates are not additive — no single total applies'
                      }
                    >
                      <span className="cursor-help font-normal text-gray-muted">
                        —
                      </span>
                    </Tooltip>
                  ) : (
                    formatValue(total / footerDivisor, isCurrency)
                  )}
                </td>
              );
            })}
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

export default SummaryGrid;
